import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { MessageSquare, FileText, LogOut, PenTool, HelpCircle } from 'lucide-react';
import { SignedIn, SignedOut, SignInButton, UserButton, SignOutButton, useUser } from '@clerk/clerk-react';
import Ask from './pages/Ask';
import Documents from './pages/Documents';
import Write from './pages/Write';
import Tour from './components/Tour';
import './App.css';
import './components/Tour.css';

function UserInfo() {
  const { user } = useUser();
  
  if (!user) return null;
  
  const displayName = user.firstName || user.fullName || user.primaryEmailAddress?.emailAddress || 'User';
  const imageUrl = user.imageUrl;
  
  return (
    <div className="user-identity-block">
      <div className="user-identity-main">
        <div className="user-avatar-wrapper">
          <div className="user-avatar-display">
            {imageUrl ? (
              <img 
                src={imageUrl} 
                alt={displayName}
                className="user-avatar-image"
              />
            ) : (
              <div className="user-avatar-placeholder">
                {displayName.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className="user-button-trigger">
            <UserButton afterSignOutUrl="/" />
          </div>
        </div>
        <div className="user-identity-details">
          <div className="user-name">{displayName}</div>
          <div className="user-workspace">SmartLouvre</div>
        </div>
      </div>
      <SignOutButton>
        <button className="user-settings-btn" type="button" title="Sign out">
          <LogOut style={{ width: '14px', height: '14px' }} />
          <span>Log out</span>
        </button>
      </SignOutButton>
    </div>
  );
}

function ConnectionStatus() {
  const [connected, setConnected] = useState(true);
  const API_URL = process.env.REACT_APP_API_URL || process.env.VITE_API_URL || 'http://localhost:8000';
  
  useEffect(() => {
    // Only check health on page load, not continuously
    const checkConnection = async () => {
      try {
        const response = await fetch(`${API_URL}/api/health`);
        if (response.ok) {
          const data = await response.json();
          setConnected(data.connected === true);
        } else {
          setConnected(false);
        }
      } catch (err) {
        setConnected(false);
      }
    };
    
    // Check once on mount
    checkConnection();
    // No interval - only check on page load
  }, [API_URL]);
  
  return (
    <div className={`connection-status ${connected ? 'connected' : 'disconnected'}`}>
      <div className="connection-indicator" />
      <span>{connected ? 'Connected to AI' : 'Disconnected'}</span>
    </div>
  );
}

function HelpButton() {
  const { isLoaded, user } = useUser();
  
  if (!isLoaded || !user) return null;
  
  return (
    <button
      className="tour-help-button"
      onClick={() => {
        if (window.startTour) {
          window.startTour();
        }
      }}
      title="Take a tour of the application"
      aria-label="Start tour"
    >
      <HelpCircle />
    </button>
  );
}

function Layout({ children }) {
  const location = useLocation();
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  useEffect(() => {
    setIsTransitioning(true);
    const timer = setTimeout(() => setIsTransitioning(false), 150);
    return () => clearTimeout(timer);
  }, [location.pathname]);
  
  return (
    <div className="app">
      <div className="app-body">
        <aside className="sidebar">
          <div className="sidebar-header">
            <span className="sidebar-title">KoolShade AI Knowledge Assistant</span>
            <ConnectionStatus />
          </div>
          <nav className="sidebar-nav">
            <Link 
              to="/ask" 
              className={`nav-item ${location.pathname === '/ask' || location.pathname === '/' ? 'active' : ''}`}
            >
              <MessageSquare className="nav-icon" />
              <span className="nav-label">Ask</span>
            </Link>
            <Link 
              to="/write" 
              className={`nav-item ${location.pathname === '/write' ? 'active' : ''}`}
            >
              <PenTool className="nav-icon" />
              <span className="nav-label">Write</span>
            </Link>
            <Link 
              to="/documents" 
              className={`nav-item ${location.pathname === '/documents' ? 'active' : ''}`}
            >
              <FileText className="nav-icon" />
              <span className="nav-label">Documents</span>
            </Link>
          </nav>
          
          <div className="sidebar-auth">
            <SignedOut>
              <SignInButton mode="modal">
                <button className="nav-item auth-button">
                  <span className="nav-label">Sign In</span>
                </button>
              </SignInButton>
            </SignedOut>
            <SignedIn>
              <UserInfo />
            </SignedIn>
          </div>
          
          <div className="sidebar-footer">
            <div className="powered-by-label">Powered by</div>
            <a
                href="https://argon-labs.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="/argon-logo.png"
                  alt="Argon"
                  className="footer-logo"
                />
              </a>
          </div>
        </aside>
        
        <main className="main-content">
          <div className={`page-transition ${isTransitioning ? 'transitioning' : ''}`}>
            {children}
          </div>
        </main>
        <HelpButton />
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  return (
    <>
      <SignedIn>
        {children}
      </SignedIn>
      <SignedOut>
        <div className="auth-required">
          <div className="auth-required-content">
            <h2>Sign in required</h2>
            <p>Please sign in to access the knowledge base.</p>
            <SignInButton mode="modal">
              <button className="btn btn-primary">Sign In</button>
            </SignInButton>
          </div>
        </div>
      </SignedOut>
    </>
  );
}

function App() {
  return (
    <Router>
      <Layout>
        <Tour />
        <Routes>
          <Route 
            path="/" 
            element={
              <ProtectedRoute>
                <Ask />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/ask" 
            element={
              <ProtectedRoute>
                <Ask />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/write" 
            element={
              <ProtectedRoute>
                <Write />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/documents" 
            element={
              <ProtectedRoute>
                <Documents />
              </ProtectedRoute>
            } 
          />
        </Routes>
      </Layout>
    </Router>
  );
}

export default App;
